import React, { memo, useEffect, useMemo, Suspense } from "react";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import AskHivetivityInterface from "@/components/ai/AskHivetivityInterface";
// Lazy-load dashboard components
const BusinessMetrics = React.lazy(() => import('@/components/dashboard/BusinessMetrics').then(m => ({ default: m.BusinessMetrics })));
const SmartSuggestions = React.lazy(() => import('@/components/dashboard/SmartSuggestions').then(m => ({ default: m.default })));
const ActivityFeed = React.lazy(() => import('@/components/dashboard/ActivityFeed').then(m => ({ default: m.default })));
const FinancialOverview = React.lazy(() => import('@/components/dashboard/FinancialOverview').then(m => ({ default: m.default })));
const ClassPerformance = React.lazy(() => import('@/components/dashboard/ClassPerformance').then(m => ({ default: m.default })));
const UpcomingClasses = React.lazy(() => import('@/components/dashboard/UpcomingClasses').then(m => ({ default: m.default })));

import { useDashboardStats, DashboardStats } from "@/hooks/useDashboardStats";
import { useOnlineStatus } from "@/utils/connectionUtils";
import { motion } from "framer-motion";
import { Separator } from "@/components/ui/separator";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { useRenderOptimizer } from "@/utils/optimizationUtils";
import ConnectionErrorAlert from "@/components/common/ConnectionErrorAlert";
import { ErrorCategory } from "@/utils/errorHandler";
import { TrendingUp } from "lucide-react";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
  }
};

const sectionVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

// Memoized components
const MemoizedDashboardHeader = memo(DashboardHeader);

const DashboardAI: React.FC = () => {
  useRenderOptimizer('DashboardAI');
  const { data: stats, isLoading, error } = useDashboardStats();
  const isOnline = useOnlineStatus();
  const { user, userRole, isAdmin } = useAuth();
  const errorHandler = useErrorHandler("DashboardAI");

  useEffect(() => {
    if (error) {
      errorHandler.handleError(error, "Failed to load dashboard statistics", {
        userId: user?.id,
        userRole,
        isAdmin: String(isAdmin),
        category: ErrorCategory.DATABASE
      }, true);
    }
  }, [error, errorHandler, user, userRole, isAdmin]);

  const safeStats = useMemo<DashboardStats>(() => stats || {
    students: 0,
    classes: 0,
    families: 0
  }, [stats]);

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30"
    >
      <div className="space-y-8 p-6 max-w-7xl mx-auto">
        {/* Connection Alert */}
        {!isOnline && (
          <motion.div variants={itemVariants}>
            <ConnectionErrorAlert />
          </motion.div>
        )}

        {/* Header Section */}
        <motion.div variants={itemVariants}>
          <ErrorBoundary componentName="DashboardHeader">
            <MemoizedDashboardHeader />
          </ErrorBoundary>
        </motion.div>

        {/* AI Section */}
        <motion.div variants={sectionVariants} className="relative">
          <ErrorBoundary componentName="AskHivetivityInterface">
            <AskHivetivityInterface />
          </ErrorBoundary>
        </motion.div>

        {/* Business Metrics - Compact Row */}
        <motion.div variants={sectionVariants}>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              <span>Studio Overview</span>
            </h3>
            <p className="text-gray-600 text-sm">Key metrics and performance indicators</p>
          </div>
          <ErrorBoundary componentName="BusinessMetrics">
            <Suspense fallback={
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Card key={i} className="h-24 animate-pulse bg-gray-100" />
                ))}
              </div>
            }>
              <BusinessMetrics />
            </Suspense>
          </ErrorBoundary>
        </motion.div>

        {/* Secondary Content Grid */}
        <motion.div variants={sectionVariants} className="grid gap-8 lg:grid-cols-3">
          
          {/* Left Column - Financial & Performance */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Financial Overview & Class Performance Row */}
            <div className="grid gap-6 md:grid-cols-2">
              <ErrorBoundary componentName="FinancialOverview">
                <Suspense fallback={<Card className="h-64 animate-pulse bg-gray-100" />}>
                  <FinancialOverview />
                </Suspense>
              </ErrorBoundary>

              <ErrorBoundary componentName="ClassPerformance">
                <Suspense fallback={<Card className="h-64 animate-pulse bg-gray-100" />}>
                  <ClassPerformance />
                </Suspense>
              </ErrorBoundary>
            </div>

            {/* Activity Feed */}
            <ErrorBoundary componentName="ActivityFeed">
              <Suspense fallback={<Card className="h-80 animate-pulse bg-gray-100" />}>
                <ActivityFeed />
              </Suspense>
            </ErrorBoundary>
          </div>

          {/* Right Column - Quick Info & Actions */}
          <div className="space-y-6">
            
            {/* Upcoming Classes */}
            <ErrorBoundary componentName="UpcomingClasses">
              <Suspense fallback={<Card className="h-80 animate-pulse bg-gray-100" />}>
                <UpcomingClasses />
              </Suspense>
            </ErrorBoundary>

            {/* Smart Suggestions - Compact */}
            <ErrorBoundary componentName="SmartSuggestions">
              <Suspense fallback={<Card className="h-48 animate-pulse bg-gray-100" />}>
                <SmartSuggestions />
              </Suspense>
            </ErrorBoundary>

          </div>
        </motion.div>

        {/* Footer Spacer */}
        <div className="h-8"></div>
      </div>
    </motion.div>
  );
};

export default DashboardAI;